/**
 * AppFactory API - Templates (Firestore SDK), Component Styles + Deploy (Turing API)
 * Templates 用 Firestore 直接讀寫，與 device types 相同，避免 401。
 */

import { auth } from './firebase'
import { getCurrentApiUrl } from './environmentStore'
import * as appfactoryFirestore from './appfactoryFirestore'

function toErrorMessage(input: unknown, fallback: string): string {
  if (typeof input === 'string' && input.trim()) return input
  if (input && typeof input === 'object') {
    const asRecord = input as Record<string, unknown>
    const nested = asRecord.message ?? asRecord.error
    if (typeof nested === 'string' && nested.trim()) return nested
    try {
      return JSON.stringify(input)
    } catch {
      return fallback
    }
  }
  return fallback
}

function isStagingLikeHost(): boolean {
  if (typeof window === 'undefined') return false
  const host = window.location.hostname.toLowerCase()
  const isPreviewPrHost = host.includes('--pr-') && host.endsWith('.web.app')
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host.includes('staging') ||
    host === 'ultron-turing-project.web.app' ||
    isPreviewPrHost
  )
}

function getApiBase(): string {
  // Dev 時走 Vite proxy（same-origin，無 CORS）；直接打 staging 會 Failed to fetch
  if (import.meta.env.DEV && !import.meta.env.VITE_USE_LOCAL_BACKEND) {
    return ''
  }
  // Staging/PR preview host 一律鎖定 staging backend，避免被 build-time VITE_TURING_URL 覆蓋到 production。
  if (isStagingLikeHost()) {
    return getCurrentApiUrl()
  }
  const override = import.meta.env.VITE_TURING_URL
  if (override && /localhost|127\.0\.0\.1/i.test(override) && !import.meta.env.VITE_USE_LOCAL_BACKEND) {
    return getCurrentApiUrl()
  }
  if (override) return override
  return getCurrentApiUrl()
}

async function getAuthHeaders(forceRefresh = false): Promise<HeadersInit> {
  const user = auth?.currentUser
  if (!user) {
    throw new Error('請先登入。Console API 需要 Firebase 認證。')
  }
  if (forceRefresh) {
    try {
      await user.reload()
    } catch {
      // Ignore transient reload errors and continue token refresh.
    }
  }
  const idToken = await user.getIdToken(forceRefresh)
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Ultron-Cloud-Appid': 'UTM',
    'Authorization': `Bearer ${idToken}`,
  }
  return headers
}

/** 強制刷新 Firebase token，供 401 後重試前呼叫 */
export async function refreshAuthToken(): Promise<void> {
  const user = auth?.currentUser
  if (!user) return
  await user.getIdToken(true)
}

export type ComponentStyle = {
  id: string
  name: string
  status: 'draft' | 'waiting_for_approval' | 'approved' | 'published' | 'rejected' | 'rolled_back'
  tokens: {
    colors?: Record<string, string>
    radius?: Record<string, number>
    spacing?: Record<string, number>
    typography?: Record<string, string | number>
  }
  createdAt?: string
  updatedAt?: string
  version?: string
}

export type Template = appfactoryFirestore.Template

export type DeployRecord = {
  id: string
  type: 'template' | 'component-style' | 'app-template' | 'device-type-modal'
  targetId: string
  targetName: string
  version: string
  status: 'waiting_for_approval' | 'approved' | 'published' | 'rejected' | 'rolled_back' | 'deprecated'
  deployedAt: string
  deployedBy?: string
  notes?: string
}

export type DeployAssetType = 'app_template' | 'device_type_modal' | 'component_style'
export type DeployVersionBump = 'major' | 'minor' | 'patch'
export type DeployVisibility = 'public' | 'permission'
export type DeployStatus =
  | 'draft'
  | 'waiting_for_approval'
  | 'approved'
  | 'published'
  | 'rejected'
  | 'rolled_back'

export type DeployVersion = {
  aa: number
  bb: number
  cc: number
  timestamp: string
  full: string
}

export type DeployPermission = {
  clientIds: string[]
  userIds: string[]
}

export type DeployItem = {
  id: string
  appId?: string
  assetType: DeployAssetType
  assetId: string
  assetName: string
  snapshot?: Record<string, unknown>
  status: DeployStatus
  versionBump?: DeployVersionBump
  version?: DeployVersion
  visibility?: DeployVisibility
  permission?: DeployPermission
  notes?: string
  requestedBy?: string
  requestedAt?: string
  approvedBy?: string
  approvedAt?: string
  publishedBy?: string
  publishedAt?: string
  rolledBackBy?: string
  rolledBackAt?: string
  rejectionReason?: string
  rollbackReason?: string
  updatedAt?: string
}

export type DeployRequestPayload = {
  versionBump: DeployVersionBump
  visibility: DeployVisibility
  permission?: Partial<DeployPermission>
  notes?: string
}

export type AppSuiteIAMClient = {
  id: string
  name: string
  externalCode?: string
  contactEmail?: string
  projectQuota?: number
  status?: string
  notes?: string
  quota?: {
    maxUsers?: number
    currentUsers?: number
    pendingInvites?: number
    updatedAt?: string
    updatedBy?: string
  } | null
  updatedAt?: string
}

export type AppSuiteIAMDeveloper = {
  id: string
  clientId: string
  userId?: string
  email: string
  name?: string
  role?: string
  status?: string
  permissions?: string[]
  invitedAt?: string
  invitedBy?: string
  updatedAt?: string
  updatedBy?: string
  lastLoginAt?: string | null
}

export type AppSuiteIAMQuota = {
  clientId: string
  maxUsers: number
  currentUsers: number
  pendingInvites: number
  updatedAt?: string | null
  updatedBy?: string | null
}

export type AppSuiteIAMUploadedUser = {
  id: string
  clientId: string
  email: string
  name?: string
  status?: string
  source?: string
  uploadedAt?: string
  uploadedBy?: string
}

export type AppSuiteUserInvitation = {
  id: string
  clientId: string
  clientName?: string
  email: string
  name?: string
  role?: string
  status?: string
  invitedAt?: string
  expiresAt?: string
  acceptedAt?: string | null
  revokedAt?: string | null
}

export type AppSuiteUserAccess = {
  hasAccess: boolean
  user?: {
    email?: string | null
    userId?: string | null
  }
  clients?: Array<{
    id: string
    name?: string
    status?: string
  }>
  defaultClientId?: string | null
}

export type AppSuiteLoginBranding = {
  id?: string
  clientId: string
  styleId: string
  backgroundColor?: string
  backgroundImageUrl?: string
  logoImageUrl?: string
  updatedAt?: string
  updatedBy?: string
}

export const APPSUITE_PROJECT_QUOTA_NOTE_REGEX = /\[projectQuota=(\d+)\]/i

function toNonNegativeInt(value: unknown, fallback = 0): number {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return fallback
  return Math.max(0, Math.floor(parsed))
}

export function parseAppSuiteProjectQuotaFromNotes(notes?: string): number {
  const match = String(notes || '').match(APPSUITE_PROJECT_QUOTA_NOTE_REGEX)
  if (!match) return 0
  return toNonNegativeInt(match[1], 0)
}

export function resolveAppSuiteClientProjectQuota(
  client: Pick<AppSuiteIAMClient, 'projectQuota' | 'notes'>
): number {
  const rawQuota = Number(client.projectQuota)
  if (Number.isFinite(rawQuota) && rawQuota >= 0) {
    return toNonNegativeInt(rawQuota, 0)
  }
  return parseAppSuiteProjectQuotaFromNotes(client.notes)
}

export function buildAppSuiteClientNotesWithProjectQuota(
  quota: number,
  existingNotes?: string
): string {
  const marker = `[projectQuota=${toNonNegativeInt(quota, 0)}]`
  const cleaned = String(existingNotes || '')
    .replace(/\[projectQuota=\d+\]/gi, '')
    .trim()
  return cleaned ? `${marker}\n${cleaned}` : marker
}

export function normalizeAppSuiteIAMClient(client: AppSuiteIAMClient): AppSuiteIAMClient {
  return {
    ...client,
    projectQuota: resolveAppSuiteClientProjectQuota(client),
  }
}

export const APPSUITE_ACTIVE_CLIENT_STORAGE_KEY = 'appsuite:selectedClientId'
export const APPSUITE_ACTIVE_CLIENT_CHANGED_EVENT = 'appsuite:active-client-changed'
const APPSUITE_LOGIN_BRANDING_STORAGE_PREFIX = 'appsuite:loginBranding:'

export function getAppSuiteActiveClientId(): string {
  if (typeof window === 'undefined') return ''
  try {
    return String(window.localStorage.getItem(APPSUITE_ACTIVE_CLIENT_STORAGE_KEY) || '').trim()
  } catch {
    return ''
  }
}

export function setAppSuiteActiveClientId(clientId: string): void {
  if (typeof window === 'undefined') return
  try {
    const value = String(clientId || '').trim()
    if (value) {
      window.localStorage.setItem(APPSUITE_ACTIVE_CLIENT_STORAGE_KEY, value)
    } else {
      window.localStorage.removeItem(APPSUITE_ACTIVE_CLIENT_STORAGE_KEY)
    }
    window.dispatchEvent(new CustomEvent(APPSUITE_ACTIVE_CLIENT_CHANGED_EVENT, { detail: { clientId: value } }))
  } catch {
    // ignore
  }
}

function getAppSuiteLoginBrandingStorageKey(clientId: string, styleId: string): string {
  return `${APPSUITE_LOGIN_BRANDING_STORAGE_PREFIX}${clientId}::${styleId}`.toLowerCase()
}

export async function listComponentStyles(): Promise<ComponentStyle[]> {
  const json = await fetchAppFactory<{ data?: ComponentStyle[]; styles?: ComponentStyle[] }>('/api/v2/appfactory/component-styles')
  return json.data || json.styles || []
}

export async function getComponentStyle(id: string): Promise<ComponentStyle | null> {
  try {
    const json = await fetchAppFactory<{ data?: ComponentStyle }>(`/api/v2/appfactory/component-styles/${encodeURIComponent(id)}`)
    return json.data || null
  } catch {
    return null
  }
}

async function fetchAppFactory<T>(path: string, init?: RequestInit, retried = false): Promise<T> {
  // App Suite user routes are auth-critical, always request a fresh token.
  const forceRefresh = retried || path.startsWith('/api/v2/appsuite/users')
  const headers = await getAuthHeaders(forceRefresh)
  let res: Response
  try {
    res = await fetch(`${getApiBase()}${path}`, { ...init, headers: { ...(init?.headers as object || {}), ...headers } })
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unknown error'
    if ((msg.includes('fetch') || msg.includes('network')) && !retried) {
      await new Promise((r) => setTimeout(r, 1500))
      return fetchAppFactory<T>(path, init, true)
    }
    throw new Error(`連線失敗：${msg}。請檢查網路或稍後再試。`)
  }
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg = toErrorMessage(json?.message ?? json?.error, `HTTP ${res.status}`)
    if (res.status === 401 && !retried) {
      await refreshAuthToken()
      await new Promise((r) => setTimeout(r, 800))
      return fetchAppFactory<T>(path, init, true)
    }
    if (res.status === 401) {
      throw new Error(`認證失敗 (401)：${msg}。請重新整理頁面或重新登入以更新 token。`)
    }
    throw new Error(msg)
  }
  if (json.success === false) throw new Error(json.error || 'Failed')
  return json as T
}

export async function listTemplates(): Promise<Template[]> {
  return appfactoryFirestore.listTemplates()
}

export async function getTemplate(id: string): Promise<Template | null> {
  return appfactoryFirestore.getTemplate(id)
}

export async function updateTemplateName(id: string, name: string): Promise<{ success: boolean; error?: string }> {
  return appfactoryFirestore.updateTemplateName(id, name)
}

export type TemplatePreset = appfactoryFirestore.TemplatePreset

export async function getTemplatePreset(id: string): Promise<TemplatePreset | null> {
  return appfactoryFirestore.getTemplatePresetWithOverrides(id)
}

export type AppFactoryTemplatePreviewLink = {
  token: string
  previewUrl: string
  expiresAt: string
  previewMode?: TemplatePreviewMode
}

export type TemplatePreviewMode = 'api_key_preview' | 'public_demo' | 'oauth_preview'

type TemplatePreviewSnapshotInput = {
  templateId: string
  sections?: Record<string, unknown>
  tokens?: Record<string, unknown>
  storeLink?: string | null
}

type DirectPreviewPayload = {
  v: 1
  templateId: string
  previewMode?: TemplatePreviewMode
  appId?: string
  apiKey?: string
  expiresAt: string
}

function toBase64Url(input: string): string {
  const encoded = btoa(unescape(encodeURIComponent(input)))
  return encoded.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function fromBase64Url(input: string): string {
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64 + '='.repeat((4 - (base64.length % 4 || 4)) % 4)
  return decodeURIComponent(escape(atob(padded)))
}

function buildDirectPreviewToken(payload: DirectPreviewPayload): string {
  return `direct.${toBase64Url(JSON.stringify(payload))}`
}

function parseDirectPreviewToken(token: string): DirectPreviewPayload | null {
  if (!token.startsWith('direct.')) return null
  const raw = token.slice('direct.'.length)
  if (!raw) return null
  try {
    const parsed = JSON.parse(fromBase64Url(raw)) as Partial<DirectPreviewPayload>
    const previewMode = (() => {
      const mode = String(parsed.previewMode || '').trim()
      if (mode === 'public_demo' || mode === 'oauth_preview' || mode === 'api_key_preview') {
        return mode
      }
      return 'api_key_preview'
    })()
    const appId = typeof parsed.appId === 'string' ? parsed.appId : ''
    const apiKey = typeof parsed.apiKey === 'string' ? parsed.apiKey : ''
    const modeNeedsAppId = previewMode === 'oauth_preview' || previewMode === 'api_key_preview'
    const modeNeedsApiKey = previewMode === 'api_key_preview'
    if (
      parsed.v !== 1 ||
      typeof parsed.templateId !== 'string' ||
      typeof parsed.expiresAt !== 'string'
    ) {
      return null
    }
    if (modeNeedsAppId && !appId) return null
    if (modeNeedsApiKey && !apiKey) return null
    return {
      v: 1,
      templateId: parsed.templateId,
      previewMode,
      appId: appId || undefined,
      apiKey: apiKey || undefined,
      expiresAt: parsed.expiresAt,
    }
  } catch {
    return null
  }
}

function safeJsonClone<T>(value: T): T | undefined {
  if (value === undefined || value === null) return undefined
  try {
    return JSON.parse(JSON.stringify(value)) as T
  } catch {
    return undefined
  }
}

export async function generateTemplatePreviewLinkByMode(
  templateId: string,
  options?: {
    previewMode?: TemplatePreviewMode
    appId?: string
    apiKey?: string
    templateSnapshot?: TemplatePreviewSnapshotInput
    modalOverrides?: Record<string, unknown>
  }
): Promise<AppFactoryTemplatePreviewLink> {
  const safeTemplateId = String(templateId || '').trim()
  const safeMode = (() => {
    const raw = String(options?.previewMode || 'api_key_preview').trim()
    if (raw === 'public_demo' || raw === 'oauth_preview' || raw === 'api_key_preview') return raw
    return 'api_key_preview'
  })()
  const safeAppId = String(options?.appId || '').trim()
  const safeApiKey = String(options?.apiKey || '').trim()
  if (!safeTemplateId) {
    throw new Error('templateId is required')
  }
  if (safeMode === 'api_key_preview' && (!safeAppId || !safeApiKey)) {
    throw new Error('appId and apiKey are required for api_key_preview')
  }
  if (safeMode === 'oauth_preview' && !safeAppId) {
    throw new Error('appId is required for oauth_preview')
  }

  const hasOptionalPayload = Boolean(options?.templateSnapshot || options?.modalOverrides)
  const templateSnapshotPayload = safeJsonClone(options?.templateSnapshot)
  const modalOverridesPayload = safeJsonClone(options?.modalOverrides)
  const requestPreviewLink = async (includeOptionalPayload: boolean) => {
    const body: Record<string, unknown> = {
      previewMode: safeMode,
    }
    if (safeAppId) body.appId = safeAppId
    if (safeApiKey) body.apiKey = safeApiKey
    if (includeOptionalPayload) {
      if (templateSnapshotPayload && typeof templateSnapshotPayload === 'object') {
        body.templateSnapshot = templateSnapshotPayload
      }
      if (modalOverridesPayload && typeof modalOverridesPayload === 'object') {
        body.modalOverrides = modalOverridesPayload
      }
    }
    return fetchAppFactory<{
      success?: boolean
      data?: { token?: string; previewUrl?: string; expiresAt?: string; previewMode?: TemplatePreviewMode }
      error?: string
    }>(
      `/api/v2/appfactory/templates/${encodeURIComponent(safeTemplateId)}/preview-link`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    )
  }

  try {
    let json
    try {
      json = await requestPreviewLink(hasOptionalPayload)
    } catch (firstError) {
      const firstMessage = firstError instanceof Error ? firstError.message : String(firstError || '')
      const shouldRetryWithoutPayload =
        hasOptionalPayload &&
        (/http 500/i.test(firstMessage) ||
          /unsupported field value/i.test(firstMessage) ||
          /invalid argument/i.test(firstMessage) ||
          /request entity too large/i.test(firstMessage))
      if (!shouldRetryWithoutPayload) {
        throw firstError
      }
      json = await requestPreviewLink(false)
    }

    const token = String(json?.data?.token || '').trim()
    const previewUrl = String(json?.data?.previewUrl || '').trim()
    const expiresAt = String(json?.data?.expiresAt || '').trim()
    if (!token || !previewUrl || !expiresAt) {
      throw new Error(json?.error || 'Failed to generate preview link')
    }
    return { token, previewUrl, expiresAt, previewMode: json?.data?.previewMode || safeMode }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error || '')
    const isAuthOrPermissionError =
      /401/.test(message) ||
      /403/.test(message) ||
      /認證失敗/i.test(message) ||
      /forbidden/i.test(message) ||
      /utm admin required/i.test(message)
    const shouldFallback =
      !isAuthOrPermissionError &&
      (
      /endpoint .* not found/i.test(message) ||
      /http 404/i.test(message) ||
      /http 5\d\d/i.test(message) ||
      /failed to create preview link/i.test(message) ||
      /failed to generate preview link/i.test(message) ||
      /internal server error/i.test(message) ||
      /server error/i.test(message) ||
      /連線失敗/i.test(message) ||
      /network/i.test(message) ||
      /failed to fetch/i.test(message) ||
      /not found/i.test(message)
      )
    if (!shouldFallback) {
      throw error
    }
    const expiresAtIso = new Date(Date.now() + 10 * 60 * 1000).toISOString()
    const directPayload: DirectPreviewPayload = {
      v: 1,
      previewMode: safeMode,
      templateId: safeTemplateId,
      expiresAt: expiresAtIso,
    }
    if (safeAppId) directPayload.appId = safeAppId
    if (safeApiKey) directPayload.apiKey = safeApiKey
    const directToken = buildDirectPreviewToken(directPayload)
    return {
      token: directToken,
      previewUrl: `/appfactory/preview-link/${encodeURIComponent(directToken)}`,
      expiresAt: expiresAtIso,
      previewMode: safeMode,
    }
  }
}

export async function generateTemplatePreviewLink(
  templateId: string,
  appId: string,
  apiKey: string,
  options?: {
    templateSnapshot?: TemplatePreviewSnapshotInput
    modalOverrides?: Record<string, unknown>
  }
): Promise<AppFactoryTemplatePreviewLink> {
  return generateTemplatePreviewLinkByMode(templateId, {
    previewMode: 'api_key_preview',
    appId,
    apiKey,
    templateSnapshot: options?.templateSnapshot,
    modalOverrides: options?.modalOverrides,
  })
}

export type AppFactoryPublicPreviewPayload = {
  templateId: string
  previewMode?: TemplatePreviewMode
  templateSnapshot: {
    templateId: string
    sections?: Record<string, unknown>
    tokens?: Record<string, unknown>
    storeLink?: string | null
    framerPresetOverrides?: Record<string, unknown>
    customCss?: string
  }
  modalOverrides: Record<string, unknown>
  credentials: {
    appId?: string
    apiKey?: string
  }
  expiresAt: string
}

export async function getPublicTemplatePreviewPayload(token: string): Promise<AppFactoryPublicPreviewPayload> {
  const safeToken = String(token || '').trim()
  if (!safeToken) {
    throw new Error('Missing preview token')
  }

  const directPayload = parseDirectPreviewToken(safeToken)
  if (directPayload) {
    const expiresAt = new Date(directPayload.expiresAt)
    if (Number.isNaN(expiresAt.getTime()) || expiresAt.getTime() < Date.now()) {
      throw new Error('Preview link expired')
    }
    const apiBase = getCurrentApiUrl()
    const presetRes = await fetch(
      `${apiBase}/appsuite-public/preset/${encodeURIComponent(directPayload.templateId)}/render-model`
    )
    const presetJson = await presetRes.json().catch(() => ({}))
    const templateData =
      presetRes.ok && presetJson?.success && presetJson?.data?.template
        ? (presetJson.data.template as { sections?: Record<string, unknown>; styles?: { tokens?: Record<string, unknown> } })
        : null
    return {
      templateId: directPayload.templateId,
      previewMode: directPayload.previewMode || 'api_key_preview',
      templateSnapshot: {
        templateId: directPayload.templateId,
        sections: templateData?.sections || {},
        tokens: templateData?.styles?.tokens || {},
        storeLink: null,
      },
      modalOverrides: {},
      credentials: {
        appId: directPayload.appId,
        apiKey: directPayload.apiKey,
      },
      expiresAt: directPayload.expiresAt,
    }
  }

  const apiBase = getCurrentApiUrl()
  const response = await fetch(`${apiBase}/api/v2/appfactory/preview-link/${encodeURIComponent(safeToken)}`)
  const data = await response.json().catch(() => ({}))
  if (!response.ok || !data?.success || !data?.data) {
    const errCode = String(data?.error || '').trim()
    if (errCode === 'TOKEN_EXPIRED') {
      throw new Error('Preview link expired')
    }
    if (errCode === 'TOKEN_NOT_FOUND' || errCode === 'TOKEN_REVOKED') {
      throw new Error('Preview link is invalid')
    }
    throw new Error(String(data?.error || data?.message || 'Failed to load preview payload'))
  }
  return data.data as AppFactoryPublicPreviewPayload
}

export type CardDeviceTypeConfig = {
  enabledTraits?: string[]
  enabledAttributes?: string[]
  enabledStates?: string[]
  traitOrder?: string[]
  attributeOrder?: string[]
  stateOrder?: string[]
  hiddenTraits?: string[]
  hiddenAttributes?: string[]
  hiddenStates?: string[]
  cardTraitStyles?: Record<string, string>
  cardAttributeStyles?: Record<string, string>
}

export type ModalDeviceTypeConfig = {
  enabledTraits?: string[]
  enabledAttributes?: string[]
  enabledStates?: string[]
  componentOrder?: string[]
  hiddenComponents?: string[]
  modalTraitStyles?: Record<string, string>
  modalAttributeStyles?: Record<string, string>
  /** Device type 專用 modal style（如 AC_UNIT Style01）取代 trait 組合 */
  modalStyleId?: string
}

export type TemplateSections = {
  card?: {
    defaultConfig?: CardDeviceTypeConfig
    deviceTypes?: Record<string, CardDeviceTypeConfig>
  }
  modal?: {
    defaultConfig?: ModalDeviceTypeConfig
    deviceTypes?: Record<string, ModalDeviceTypeConfig>
  }
  login?: {
    styleId?: string
  }
  editor?: Record<string, unknown>
}

export async function updateTemplateSections(id: string, sections: TemplateSections): Promise<{ success: boolean; error?: string }> {
  return appfactoryFirestore.updateTemplateSections(id, sections as appfactoryFirestore.TemplateSections)
}

export async function listComponentStyleNameOverrides(): Promise<Record<string, string>> {
  return appfactoryFirestore.listComponentStyleNameOverrides()
}

export async function updateComponentStyleName(
  styleKey: string,
  styleName: string
): Promise<{ success: boolean; error?: string }> {
  return appfactoryFirestore.updateComponentStyleName(styleKey, styleName)
}

export async function getCustomCss(): Promise<string> {
  return appfactoryFirestore.getCustomCss()
}

export async function updateCustomCss(css: string): Promise<{ success: boolean; error?: string }> {
  return appfactoryFirestore.updateCustomCss(css)
}

export type FramerPresetOverrides = appfactoryFirestore.FramerPresetOverrides

export async function getFramerPresetOverrides(): Promise<FramerPresetOverrides> {
  return appfactoryFirestore.getFramerPresetOverrides()
}

export async function updateFramerPresetOverride(
  preset: string,
  vars: FramerPresetOverrides[string]
): Promise<{ success: boolean; error?: string }> {
  return appfactoryFirestore.updateFramerPresetOverride(preset, vars)
}

export async function deleteFramerPresetOverride(preset: string): Promise<{ success: boolean; error?: string }> {
  return appfactoryFirestore.deleteFramerPresetOverride(preset)
}

export type LoginPagePresetOverrides = appfactoryFirestore.LoginPagePresetOverrides

export async function getLoginPagePresetOverrides(): Promise<LoginPagePresetOverrides> {
  return appfactoryFirestore.getLoginPagePresetOverrides()
}

export async function updateLoginPagePresetOverride(
  styleId: string,
  vars: LoginPagePresetOverrides[string]
): Promise<{ success: boolean; error?: string }> {
  return appfactoryFirestore.updateLoginPagePresetOverride(styleId, vars)
}

export async function deleteLoginPagePresetOverride(styleId: string): Promise<{ success: boolean; error?: string }> {
  return appfactoryFirestore.deleteLoginPagePresetOverride(styleId)
}

// Design Tokens (Phase 5)
export type DesignTokens = appfactoryFirestore.DesignTokens

export async function getDesignTokens(): Promise<DesignTokens> {
  return appfactoryFirestore.getDesignTokens()
}

export async function updateDesignTokens(tokens: DesignTokens): Promise<{ success: boolean; error?: string }> {
  return appfactoryFirestore.updateDesignTokens(tokens)
}

export type DeviceTypeModalOverride = appfactoryFirestore.DeviceTypeModalOverride
export type FramePreset = appfactoryFirestore.FramePreset

export async function listDeviceTypeModalOverrides(): Promise<Record<string, appfactoryFirestore.DeviceTypeModalOverride>> {
  return appfactoryFirestore.listDeviceTypeModalOverrides()
}

export async function updateDeviceTypeModalOverride(
  canonicalStyleId: string,
  payload: Partial<appfactoryFirestore.DeviceTypeModalOverride>
): Promise<{ success: boolean; error?: string }> {
  return appfactoryFirestore.updateDeviceTypeModalOverride(canonicalStyleId, payload)
}

export async function deleteDeviceTypeModalOverride(
  canonicalStyleId: string
): Promise<{ success: boolean; error?: string }> {
  return appfactoryFirestore.deleteDeviceTypeModalOverride(canonicalStyleId)
}

export async function getCredentialFromUserProfile(): Promise<{ appId: string; apiKey: string } | null> {
  return appfactoryFirestore.getCredentialFromUserProfile()
}

export async function setCredentialInUserProfile(
  appId: string,
  apiKey: string
): Promise<{ success: boolean; error?: string }> {
  return appfactoryFirestore.setCredentialInUserProfile(appId, apiKey)
}

export async function clearCredentialFromUserProfile(): Promise<{ success: boolean; error?: string }> {
  return appfactoryFirestore.clearCredentialFromUserProfile()
}

export async function deployTemplate(id: string, version: string, notes?: string): Promise<{ success: boolean; error?: string }> {
  const json = await fetchAppFactory<{ success?: boolean; error?: string }>(
    `/api/v2/appfactory/templates/${encodeURIComponent(id)}/deploy`,
    { method: 'POST', body: JSON.stringify({ version, notes }) }
  )
  return { success: json.success !== false, error: json.error }
}

export async function deployComponentStyle(id: string, version: string, notes?: string): Promise<{ success: boolean; error?: string }> {
  const json = await fetchAppFactory<{ success?: boolean; error?: string }>(
    `/api/v2/appfactory/component-styles/${encodeURIComponent(id)}/deploy`,
    { method: 'POST', body: JSON.stringify({ version, notes }) }
  )
  return { success: json.success !== false, error: json.error }
}

export async function listDeployHistory(): Promise<DeployRecord[]> {
  const json = await fetchAppFactory<{ data?: DeployRecord[]; history?: DeployRecord[] }>('/api/v2/appfactory/deploy/history')
  return json.data || json.history || []
}

/** Published assets visible to the current user (filtered by visibility/permission, deduplicated) */
export async function listAnnouncements(): Promise<DeployRecord[]> {
  const json = await fetchAppSuite<{ data?: DeployRecord[] }>('/announcements')
  return json.data || []
}

export async function listPublishedTemplates(): Promise<Template[]> {
  const json = await fetchAppFactory<{ data?: Template[] }>('/api/v2/appfactory/deploy/published-templates')
  return json.data || []
}

export async function listPublishedComponentStyles(): Promise<ComponentStyle[]> {
  const json = await fetchAppFactory<{ data?: ComponentStyle[] }>('/api/v2/appfactory/deploy/published-component-styles')
  return json.data || []
}

export async function listPublishedDeviceTypeModals(): Promise<Record<string, unknown>[]> {
  const json = await fetchAppFactory<{ data?: Record<string, unknown>[] }>('/api/v2/appfactory/deploy/published-device-type-modals')
  return json.data || []
}

export async function listAppSuitePublishedTemplates(): Promise<Template[]> {
  const clientId = getAppSuiteActiveClientId()
  const qs = clientId ? `?clientId=${encodeURIComponent(clientId)}` : ''
  const json = await fetchAppFactory<{ templates?: Template[]; data?: Template[] }>(
    `/api/v2/appsuite/published-templates${qs}`
  )
  return json.templates || json.data || []
}

export async function listAppSuitePublishedComponentStyles(): Promise<ComponentStyle[]> {
  const clientId = getAppSuiteActiveClientId()
  const qs = clientId ? `?clientId=${encodeURIComponent(clientId)}` : ''
  const json = await fetchAppFactory<{ styles?: ComponentStyle[]; data?: ComponentStyle[] }>(
    `/api/v2/appsuite/published-component-styles${qs}`
  )
  return json.styles || json.data || []
}

export async function listAppSuitePublishedDeviceTypeModals(): Promise<Record<string, unknown>[]> {
  const clientId = getAppSuiteActiveClientId()
  const qs = clientId ? `?clientId=${encodeURIComponent(clientId)}` : ''
  const json = await fetchAppFactory<{ items?: Record<string, unknown>[]; data?: Record<string, unknown>[] }>(
    `/api/v2/appsuite/published-device-type-modals${qs}`
  )
  return json.items || json.data || []
}

export async function listDeployItems(filters?: {
  status?: DeployStatus
  assetType?: DeployAssetType
  limit?: number
}): Promise<DeployItem[]> {
  const params = new URLSearchParams()
  if (filters?.status) params.set('status', filters.status)
  if (filters?.assetType) params.set('assetType', filters.assetType)
  if (filters?.limit) params.set('limit', String(filters.limit))
  const query = params.toString()
  const json = await fetchAppFactory<{ data?: DeployItem[] }>(`/api/v2/appfactory/deploy${query ? `?${query}` : ''}`)
  return json.data || []
}

export async function getNextDeployVersion(
  assetType: DeployAssetType,
  assetId: string,
  versionBump: DeployVersionBump
): Promise<DeployVersion | null> {
  const params = new URLSearchParams({
    assetType,
    assetId,
    versionBump,
  })
  const json = await fetchAppFactory<{ data?: DeployVersion }>(`/api/v2/appfactory/deploy/next-version?${params.toString()}`)
  return json.data || null
}

async function requestDeployByAsset(
  endpointPath: string,
  payload: DeployRequestPayload
): Promise<{ success: boolean; error?: string; item?: DeployItem }> {
  const json = await fetchAppFactory<{ success?: boolean; error?: string; data?: DeployItem }>(
    endpointPath,
    {
      method: 'POST',
      body: JSON.stringify({
        versionBump: payload.versionBump,
        visibility: payload.visibility,
        permission: {
          clientIds: payload.permission?.clientIds || [],
          userIds: payload.permission?.userIds || [],
        },
        notes: payload.notes || '',
      }),
    }
  )
  return { success: json.success !== false, error: json.error, item: json.data }
}

export async function requestTemplateDeploy(
  templateId: string,
  payload: DeployRequestPayload
): Promise<{ success: boolean; error?: string; item?: DeployItem }> {
  return requestDeployByAsset(`/api/v2/appfactory/templates/${encodeURIComponent(templateId)}/deploy-request`, payload)
}

export async function requestComponentStyleDeploy(
  styleId: string,
  payload: DeployRequestPayload
): Promise<{ success: boolean; error?: string; item?: DeployItem }> {
  return requestDeployByAsset(`/api/v2/appfactory/component-styles/${encodeURIComponent(styleId)}/deploy-request`, payload)
}

export async function requestDeviceTypeModalDeploy(
  styleId: string,
  payload: DeployRequestPayload
): Promise<{ success: boolean; error?: string; item?: DeployItem }> {
  return requestDeployByAsset(`/api/v2/appfactory/device-type-modals/${encodeURIComponent(styleId)}/deploy-request`, payload)
}

async function transitionDeployItem(
  deployId: string,
  action: 'approve' | 'reject' | 'publish' | 'rollback',
  body?: Record<string, unknown>
): Promise<{ success: boolean; error?: string; item?: DeployItem }> {
  const json = await fetchAppFactory<{ success?: boolean; error?: string; data?: DeployItem }>(
    `/api/v2/appfactory/deploy/${encodeURIComponent(deployId)}/${action}`,
    {
      method: 'POST',
      body: JSON.stringify(body || {}),
    }
  )
  return { success: json.success !== false, error: json.error, item: json.data }
}

export async function approveDeployItem(deployId: string) {
  return transitionDeployItem(deployId, 'approve')
}

export async function rejectDeployItem(deployId: string, reason?: string) {
  return transitionDeployItem(deployId, 'reject', { reason: reason || '' })
}

export async function publishDeployItem(deployId: string) {
  return transitionDeployItem(deployId, 'publish')
}

export async function rollbackDeployItem(deployId: string, reason?: string) {
  return transitionDeployItem(deployId, 'rollback', { reason: reason || '' })
}

async function fetchAppSuiteIAM<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAppFactory<T>(`/api/v2/appsuite/iam${path}`, init)
}

export async function listIAMClients(): Promise<AppSuiteIAMClient[]> {
  const json = await fetchAppSuiteIAM<{ data?: AppSuiteIAMClient[] }>('/clients')
  return (json.data || []).map((client) => normalizeAppSuiteIAMClient(client))
}

export async function createIAMClient(payload: {
  clientId?: string
  name: string
  externalCode?: string
  contactEmail?: string
  projectQuota?: number
  notes?: string
  status?: string
  maxUsers?: number
}): Promise<{ success: boolean; error?: string; client?: AppSuiteIAMClient }> {
  const json = await fetchAppSuiteIAM<{ success?: boolean; error?: string; data?: AppSuiteIAMClient }>(
    '/clients',
    { method: 'POST', body: JSON.stringify(payload) }
  )
  return { success: json.success !== false, error: json.error, client: json.data }
}

export async function updateIAMClient(
  clientId: string,
  payload: Partial<
    Pick<AppSuiteIAMClient, 'name' | 'externalCode' | 'contactEmail' | 'projectQuota' | 'notes' | 'status'>
  >
): Promise<{ success: boolean; error?: string; client?: AppSuiteIAMClient }> {
  const json = await fetchAppSuiteIAM<{ success?: boolean; error?: string; data?: AppSuiteIAMClient }>(
    `/clients/${encodeURIComponent(clientId)}`,
    { method: 'PATCH', body: JSON.stringify(payload) }
  )
  return { success: json.success !== false, error: json.error, client: json.data }
}

export async function listIAMClientDevelopers(clientId: string): Promise<AppSuiteIAMDeveloper[]> {
  const json = await fetchAppSuiteIAM<{ data?: AppSuiteIAMDeveloper[] }>(`/clients/${encodeURIComponent(clientId)}/developers`)
  return json.data || []
}

export async function inviteIAMClientDeveloper(
  clientId: string,
  payload: {
    userId?: string
    email: string
    name?: string
    role?: string
    status?: string
    permissions?: string[]
  }
): Promise<{ success: boolean; error?: string; developer?: AppSuiteIAMDeveloper }> {
  const json = await fetchAppSuiteIAM<{ success?: boolean; error?: string; data?: AppSuiteIAMDeveloper }>(
    `/clients/${encodeURIComponent(clientId)}/developers`,
    { method: 'POST', body: JSON.stringify(payload) }
  )
  return { success: json.success !== false, error: json.error, developer: json.data }
}

export async function updateIAMClientDeveloper(
  clientId: string,
  developerId: string,
  payload: Partial<AppSuiteIAMDeveloper>
): Promise<{ success: boolean; error?: string; developer?: AppSuiteIAMDeveloper }> {
  const json = await fetchAppSuiteIAM<{ success?: boolean; error?: string; data?: AppSuiteIAMDeveloper }>(
    `/clients/${encodeURIComponent(clientId)}/developers/${encodeURIComponent(developerId)}`,
    { method: 'PATCH', body: JSON.stringify(payload) }
  )
  return { success: json.success !== false, error: json.error, developer: json.data }
}

export async function getIAMClientQuota(clientId: string): Promise<AppSuiteIAMQuota | null> {
  const json = await fetchAppSuiteIAM<{ data?: AppSuiteIAMQuota }>(`/clients/${encodeURIComponent(clientId)}/quota`)
  return json.data || null
}

export async function setIAMClientQuota(
  clientId: string,
  payload: { maxUsers: number; pendingInvites?: number }
): Promise<{ success: boolean; error?: string; quota?: AppSuiteIAMQuota }> {
  const json = await fetchAppSuiteIAM<{ success?: boolean; error?: string; data?: AppSuiteIAMQuota }>(
    `/clients/${encodeURIComponent(clientId)}/quota`,
    { method: 'PUT', body: JSON.stringify(payload) }
  )
  return { success: json.success !== false, error: json.error, quota: json.data }
}

export async function listIAMUploadedUsers(clientId: string): Promise<AppSuiteIAMUploadedUser[]> {
  const json = await fetchAppSuiteIAM<{ data?: AppSuiteIAMUploadedUser[] }>(
    `/clients/${encodeURIComponent(clientId)}/uploaded-users`
  )
  return json.data || []
}

export async function uploadIAMUsers(
  clientId: string,
  users: Array<{ email: string; name?: string; status?: string; source?: string }>
): Promise<{ success: boolean; error?: string; imported?: number }> {
  const json = await fetchAppSuiteIAM<{ success?: boolean; error?: string; imported?: number }>(
    `/clients/${encodeURIComponent(clientId)}/uploaded-users`,
    { method: 'POST', body: JSON.stringify({ users }) }
  )
  return { success: json.success !== false, error: json.error, imported: json.imported }
}

async function fetchAppSuiteUsers<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAppFactory<T>(`/api/v2/appsuite/users${path}`, init)
}

export async function listAppSuiteUserClients(): Promise<AppSuiteIAMClient[]> {
  const json = await fetchAppSuiteUsers<{ clients?: AppSuiteIAMClient[] }>('/clients')
  return json.clients || []
}

export async function listAppSuiteUsersByClient(clientId: string): Promise<AppSuiteIAMDeveloper[]> {
  const json = await fetchAppSuiteUsers<{ users?: AppSuiteIAMDeveloper[] }>(
    `/client/${encodeURIComponent(clientId)}/developers`
  )
  return json.users || []
}

export async function inviteAppSuiteUser(
  clientId: string,
  payload: {
    userId?: string
    email: string
    name?: string
    role?: string
    status?: string
    permissions?: string[]
  }
): Promise<{ success: boolean; error?: string; user?: AppSuiteIAMDeveloper }> {
  const json = await fetchAppSuiteUsers<{ success?: boolean; error?: string; user?: AppSuiteIAMDeveloper }>(
    `/client/${encodeURIComponent(clientId)}/developers`,
    { method: 'POST', body: JSON.stringify(payload) }
  )
  return { success: json.success !== false, error: json.error, user: json.user }
}

export async function createAppSuiteUserInvitation(
  clientId: string,
  payload: {
    email: string
    name?: string
    role?: string
    inviterName?: string
  }
): Promise<{
  success: boolean
  error?: string
  invitation?: AppSuiteUserInvitation
  emailSent?: boolean
  emailReason?: string | null
}> {
  const json = await fetchAppSuiteUsers<{
    success?: boolean
    error?: string
    invitation?: AppSuiteUserInvitation
    emailSent?: boolean
    emailReason?: string | null
  }>(
    `/client/${encodeURIComponent(clientId)}/invitations`,
    { method: 'POST', body: JSON.stringify(payload) }
  )
  return {
    success: json.success !== false,
    error: json.error,
    invitation: json.invitation,
    emailSent: json.emailSent,
    emailReason: json.emailReason,
  }
}

export async function listAppSuiteUserInvitations(clientId: string): Promise<AppSuiteUserInvitation[]> {
  const json = await fetchAppSuiteUsers<{ invitations?: AppSuiteUserInvitation[] }>(
    `/client/${encodeURIComponent(clientId)}/invitations`
  )
  return json.invitations || []
}

export async function acceptAppSuiteUserInvitation(
  token: string
): Promise<{
  success: boolean
  error?: string
  membership?: {
    clientId: string
    email: string
    role?: string
    status?: string
  }
}> {
  const json = await fetchAppSuiteUsers<{
    success?: boolean
    error?: string
    membership?: {
      clientId: string
      email: string
      role?: string
      status?: string
    }
  }>('/invitations/accept', { method: 'POST', body: JSON.stringify({ token }) })
  return {
    success: json.success !== false,
    error: json.error,
    membership: json.membership,
  }
}

export async function getAppSuiteUserAccess(): Promise<AppSuiteUserAccess> {
  // Use /users/clients as the compatibility source of truth.
  // Some environments may not have /users/me/access aligned yet.
  const clients = await listAppSuiteUserClients()
  const preferred = getAppSuiteActiveClientId()
  const defaultClientId =
    (preferred && clients.some((client) => client.id === preferred) ? preferred : clients[0]?.id) || null
  const currentUser = auth?.currentUser
  const json: AppSuiteUserAccess = {
    hasAccess: clients.length > 0,
    user: {
      email: currentUser?.email || null,
      userId: currentUser?.uid || null,
    },
    clients: clients.map((client) => ({
      id: String(client.id || '').trim(),
      name: String(client.name || '').trim() || String(client.id || '').trim(),
      status: String(client.status || '').trim() || 'active',
    })),
    defaultClientId,
  }
  return {
    hasAccess: Boolean(json.hasAccess),
    user: json.user,
    clients: json.clients || [],
    defaultClientId: json.defaultClientId || null,
  }
}

export async function getAppSuiteUserQuota(clientId: string): Promise<AppSuiteIAMQuota | null> {
  const json = await fetchAppSuiteUsers<{ quota?: AppSuiteIAMQuota }>(`/client/${encodeURIComponent(clientId)}/quota`)
  return json.quota || null
}

export async function setAppSuiteUserQuota(
  clientId: string,
  payload: { maxUsers: number; pendingInvites?: number }
): Promise<{ success: boolean; error?: string; quota?: AppSuiteIAMQuota }> {
  const json = await fetchAppSuiteUsers<{ success?: boolean; error?: string; quota?: AppSuiteIAMQuota }>(
    `/client/${encodeURIComponent(clientId)}/quota`,
    { method: 'PUT', body: JSON.stringify(payload) }
  )
  return { success: json.success !== false, error: json.error, quota: json.quota }
}

export async function listAppSuiteUploadedUsers(clientId: string): Promise<AppSuiteIAMUploadedUser[]> {
  const json = await fetchAppSuiteUsers<{ users?: AppSuiteIAMUploadedUser[] }>(
    `/client/${encodeURIComponent(clientId)}/uploaded-users`
  )
  return json.users || []
}

export async function uploadAppSuiteUsers(
  clientId: string,
  users: Array<{ email: string; name?: string; status?: string; source?: string }>
): Promise<{ success: boolean; error?: string; imported?: number }> {
  const json = await fetchAppSuiteUsers<{ success?: boolean; error?: string; imported?: number }>(
    `/client/${encodeURIComponent(clientId)}/uploaded-users`,
    { method: 'POST', body: JSON.stringify({ users }) }
  )
  return { success: json.success !== false, error: json.error, imported: json.imported }
}

export async function getAppSuiteLoginBranding(
  clientId: string,
  styleId: string
): Promise<AppSuiteLoginBranding | null> {
  const cid = String(clientId || '').trim()
  const sid = String(styleId || '').trim()
  if (!cid || !sid) return null
  try {
    const json = await fetchAppSuiteUsers<{ branding?: AppSuiteLoginBranding }>(
      `/client/${encodeURIComponent(cid)}/login-branding/${encodeURIComponent(sid)}`
    )
    return json.branding || null
  } catch {
    if (typeof window === 'undefined') return null
    try {
      const key = getAppSuiteLoginBrandingStorageKey(cid, sid)
      const raw = window.localStorage.getItem(key)
      if (!raw) return null
      const parsed = JSON.parse(raw) as AppSuiteLoginBranding
      return parsed || null
    } catch {
      return null
    }
  }
}

export async function setAppSuiteLoginBranding(
  clientId: string,
  styleId: string,
  payload: {
    backgroundColor?: string
    backgroundImageUrl?: string
    logoImageUrl?: string
  }
): Promise<{ success: boolean; error?: string; branding?: AppSuiteLoginBranding }> {
  const cid = String(clientId || '').trim()
  const sid = String(styleId || '').trim()
  if (!cid || !sid) return { success: false, error: 'clientId and styleId are required' }
  try {
    const json = await fetchAppSuiteUsers<{ success?: boolean; error?: string; branding?: AppSuiteLoginBranding }>(
      `/client/${encodeURIComponent(cid)}/login-branding/${encodeURIComponent(sid)}`,
      { method: 'PUT', body: JSON.stringify(payload) }
    )
    return {
      success: json.success !== false,
      error: json.error,
      branding: json.branding,
    }
  } catch {
    const branding: AppSuiteLoginBranding = {
      clientId: cid,
      styleId: sid,
      backgroundColor: String(payload.backgroundColor || ''),
      backgroundImageUrl: String(payload.backgroundImageUrl || ''),
      logoImageUrl: String(payload.logoImageUrl || ''),
      updatedAt: new Date().toISOString(),
      updatedBy: 'local_fallback',
    }
    if (typeof window !== 'undefined') {
      try {
        const key = getAppSuiteLoginBrandingStorageKey(cid, sid)
        window.localStorage.setItem(key, JSON.stringify(branding))
      } catch {
        // ignore
      }
    }
    return { success: true, branding }
  }
}

export type AppSuiteAvailableProjectId = {
  clientId: string
  clientName: string
  projectQuota: number
  usedProjects: number
  remainingProjects: number
  canCreate: boolean
}

export type AppSuiteProject = {
  id: string
  appId: string
  name: string
  platform?: string
  status?: string
  clientId?: string
  clientProjectId?: string
  createdBy?: string
  createdByName?: string
  updatedAt?: string
  createdAt?: string
  templateSource?: { templateId?: string; templateVersion?: string } | null
  templateSnapshot?: Record<string, unknown> | null
  styleOverrides?: Record<string, unknown> | null
  projectConfig?: Record<string, unknown> | null
}

export type AppSuiteLibraryAsset = {
  id: string
  appId: string
  clientId: string
  assetType: 'app_template' | 'device_type_modal' | 'component_style'
  sourceAssetId: string
  sourceVersion?: string
  sourcePublishedAt?: string
  name: string
  baseName?: string
  cloneIndex?: number
  bundleId?: string | null
  parentTemplateSourceId?: string | null
  parentDeviceTypeSourceId?: string | null
  snapshot?: Record<string, unknown>
  status?: string
  createdAt?: string
  updatedAt?: string
}

export type AppSuiteLibraryTreeNode = {
  template: AppSuiteLibraryAsset
  deviceTypeModals: AppSuiteLibraryAsset[]
  componentStyles: AppSuiteLibraryAsset[]
}

function fetchAppSuite<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAppFactory<T>(`/api/v2/appsuite${path}`, init)
}

export async function getAppSuiteProject(projectId: string): Promise<AppSuiteProject | null> {
  const safeId = String(projectId || '').trim()
  if (!safeId) return null
  try {
    const json = await fetchAppSuite<{ success?: boolean; project?: AppSuiteProject }>(
      `/projects/${encodeURIComponent(safeId)}`
    )
    return json.project ?? null
  } catch {
    return null
  }
}

export async function listAppSuiteAvailableProjectIds(): Promise<AppSuiteAvailableProjectId[]> {
  const json = await fetchAppSuite<{ items?: AppSuiteAvailableProjectId[]; data?: AppSuiteAvailableProjectId[] }>(
    '/projects/available-clients'
  )
  return json.items || json.data || []
}

export async function listAppSuiteProjects(): Promise<AppSuiteProject[]> {
  const json = await fetchAppSuite<{ projects?: AppSuiteProject[]; data?: AppSuiteProject[] }>('/projects')
  return json.projects || json.data || []
}

export async function createAppSuiteProject(payload: {
  name: string
  platform?: string
  clientId: string
}): Promise<{ success: boolean; error?: string; project?: AppSuiteProject }> {
  const json = await fetchAppSuite<{ success?: boolean; error?: string; project?: AppSuiteProject }>(
    '/projects',
    { method: 'POST', body: JSON.stringify(payload) }
  )
  return { success: json.success !== false, error: json.error, project: json.project }
}

export async function forkAppSuiteProjectFromTemplate(payload: {
  projectId: string
  templateId: string
  templateVersion?: string
  templateSnapshot?: Record<string, unknown>
}): Promise<{ success: boolean; error?: string; project?: AppSuiteProject }> {
  const projectId = String(payload.projectId || '').trim()
  if (!projectId) return { success: false, error: 'projectId is required' }
  const json = await fetchAppSuite<{ success?: boolean; error?: string; project?: AppSuiteProject }>(
    `/projects/${encodeURIComponent(projectId)}/fork-from-template`,
    {
      method: 'POST',
      body: JSON.stringify({
        templateId: payload.templateId,
        templateVersion: payload.templateVersion,
        templateSnapshot: payload.templateSnapshot,
      }),
    }
  )
  return { success: json.success !== false, error: json.error, project: json.project }
}

export async function deleteAppSuiteProject(projectId: string): Promise<{ success: boolean; error?: string }> {
  const safeId = String(projectId || '').trim()
  if (!safeId) return { success: false, error: 'projectId is required' }
  const json = await fetchAppSuite<{ success?: boolean; error?: string }>(
    `/projects/${encodeURIComponent(safeId)}`,
    { method: 'DELETE' }
  )
  return { success: json.success !== false, error: json.error }
}

export async function updateAppSuiteProjectConfig(
  projectId: string,
  patch: Record<string, unknown>
): Promise<{ success: boolean; error?: string }> {
  const safeId = String(projectId || '').trim()
  if (!safeId) return { success: false, error: 'projectId is required' }
  const json = await fetchAppSuite<{ success?: boolean; error?: string }>(
    `/projects/${encodeURIComponent(safeId)}/config`,
    { method: 'PATCH', body: JSON.stringify({ projectConfig: patch }) }
  )
  return { success: json.success !== false, error: json.error }
}

export async function updateAppSuiteProjectStyleOverrides(
  projectId: string,
  patch: Record<string, unknown>
): Promise<{ success: boolean; error?: string }> {
  const safeId = String(projectId || '').trim()
  if (!safeId) return { success: false, error: 'projectId is required' }
  const json = await fetchAppSuite<{ success?: boolean; error?: string }>(
    `/projects/${encodeURIComponent(safeId)}/style-overrides`,
    { method: 'PATCH', body: JSON.stringify({ styleOverrides: patch }) }
  )
  return { success: json.success !== false, error: json.error }
}

export type AppSuiteDeviceType = {
  deviceType: string
  deviceTypeId: string
  deviceTypeName: string
  iconUrl?: string
}

export async function listAppSuiteClientDeviceTypes(clientId: string): Promise<AppSuiteDeviceType[]> {
  const json = await fetchAppSuite<{ deviceTypes?: AppSuiteDeviceType[] }>(
    `/clients/${encodeURIComponent(clientId)}/device-types`
  )
  return json.deviceTypes ?? []
}

export async function deployAppSuiteProject(payload: {
  projectId: string
  target?: 'production' | 'preview'
  provider?: 'firebase' | 'vercel'
  releaseNotes?: string
  productId?: string
}): Promise<{ success: boolean; error?: string; releaseId?: string }> {
  const json = await fetchAppSuite<{ success?: boolean; error?: string; release?: { id?: string } }>('/deploy', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return {
    success: json.success !== false,
    error: json.error,
    releaseId: json.release?.id,
  }
}

export async function addAppSuitePublishedAssetToLibrary(payload: {
  clientId: string
  assetType: 'app_template' | 'device_type_modal' | 'component_style'
  assetId: string
}): Promise<{
  success: boolean
  error?: string
  asset?: AppSuiteLibraryAsset
  bundle?: { id: string; cloneIndex?: number; sourceTemplateId?: string }
  assets?: AppSuiteLibraryAsset[]
  summary?: { templates?: number; deviceTypeModals?: number; componentStyles?: number; total?: number }
}> {
  const json = await fetchAppSuite<{
    success?: boolean
    error?: string
    asset?: AppSuiteLibraryAsset
    bundle?: { id: string; cloneIndex?: number; sourceTemplateId?: string }
    assets?: AppSuiteLibraryAsset[]
    summary?: { templates?: number; deviceTypeModals?: number; componentStyles?: number; total?: number }
  }>('/library/add', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return {
    success: json.success !== false,
    error: json.error,
    asset: json.asset,
    bundle: json.bundle,
    assets: json.assets,
    summary: json.summary,
  }
}

export async function listAppSuiteLibraryAssets(params: {
  clientId: string
  assetType?: 'app_template' | 'device_type_modal' | 'component_style'
  bundleId?: string
}): Promise<AppSuiteLibraryAsset[]> {
  const query = new URLSearchParams()
  query.set('clientId', params.clientId)
  if (params.assetType) query.set('assetType', params.assetType)
  if (params.bundleId) query.set('bundleId', params.bundleId)
  const json = await fetchAppSuite<{ items?: AppSuiteLibraryAsset[]; data?: AppSuiteLibraryAsset[] }>(
    `/library/assets?${query.toString()}`
  )
  return json.items || json.data || []
}

export async function listAppSuiteLibraryTree(params: {
  clientId: string
}): Promise<{ assets: AppSuiteLibraryAsset[]; tree: AppSuiteLibraryTreeNode[] }> {
  const query = new URLSearchParams()
  query.set('clientId', params.clientId)
  const json = await fetchAppSuite<{
    assets?: AppSuiteLibraryAsset[]
    tree?: AppSuiteLibraryTreeNode[]
  }>(`/library/tree?${query.toString()}`)
  return {
    assets: json.assets || [],
    tree: json.tree || [],
  }
}

export async function removeAppSuiteLibraryAsset(params: {
  clientId: string
  assetId: string
}): Promise<{ success: boolean; error?: string; id?: string }> {
  const query = new URLSearchParams()
  query.set('clientId', params.clientId)
  const json = await fetchAppSuite<{ success?: boolean; error?: string; id?: string }>(
    `/library/assets/${encodeURIComponent(params.assetId)}?${query.toString()}`,
    { method: 'DELETE' }
  )
  return {
    success: json.success !== false,
    error: json.error,
    id: json.id,
  }
}

// ── Brand Config + Deploy ─────────────────────────────────────────────────────

export type AppSuiteBrandConfig = {
  clientId: string
  assets: {
    loginBg?: string | null
    dashboardBg?: string | null
  }
  links: {
    legal: {
      privacyPolicy?: string | null
      termsOfService?: string | null
    }
    ecommerce: Array<{ label: string; url: string }>
  }
  updatedAt?: string
}

export type AppSuiteReleaseChannel = {
  id: string
  appId: string
  target: string
  productId?: string | null
  provider?: string
  activeReleaseId?: string
  activeVersion?: string
  runtimeUrl?: string | null
  expiresAt?: string | null
  updatedAt?: string
  updatedBy?: string
}

export async function getClientBrandConfig(clientId: string): Promise<AppSuiteBrandConfig> {
  const json = await fetchAppSuiteUsers<{ config?: AppSuiteBrandConfig }>(
    `/client/${encodeURIComponent(clientId)}/brand-config`
  )
  return json.config ?? { clientId, assets: {}, links: { legal: {}, ecommerce: [] } }
}

export async function setClientBrandConfig(
  clientId: string,
  config: Omit<AppSuiteBrandConfig, 'clientId' | 'updatedAt'>
): Promise<{ success: boolean; error?: string; config?: AppSuiteBrandConfig }> {
  const json = await fetchAppSuiteUsers<{ success?: boolean; error?: string; config?: AppSuiteBrandConfig }>(
    `/client/${encodeURIComponent(clientId)}/brand-config`,
    { method: 'PUT', body: JSON.stringify(config) }
  )
  return { success: json.success !== false, error: json.error, config: json.config }
}

export async function getClientChannels(clientId: string): Promise<AppSuiteReleaseChannel[]> {
  const json = await fetchAppSuiteUsers<{ channels?: AppSuiteReleaseChannel[] }>(
    `/client/${encodeURIComponent(clientId)}/channels`
  )
  return json.channels ?? []
}

export type AppSuiteClientApp = {
  id: string
  clientId: string
  appId: string
  name: string
  authType?: 'firebase' | 'apikey' | null
  maskedApiKey?: string | null
  uid?: string | null
  email?: string | null
  tokenExpiresAt?: string | null
  active: boolean
  createdAt?: string | null
  createdBy?: string | null
}

export async function listClientApps(clientId: string): Promise<AppSuiteClientApp[]> {
  const json = await fetchAppSuiteUsers<{ apps?: AppSuiteClientApp[] }>(
    `/client/${encodeURIComponent(clientId)}/apps`
  )
  return json.apps || []
}

export async function registerClientApp(
  clientId: string,
  payload: { appId: string; apiKey: string; name?: string }
): Promise<AppSuiteClientApp> {
  const json = await fetchAppSuiteUsers<{ app?: AppSuiteClientApp }>(
    `/client/${encodeURIComponent(clientId)}/apps`,
    { method: 'POST', body: JSON.stringify(payload) }
  )
  if (!json.app) throw new Error('Failed to register client app')
  return json.app
}

export async function deleteClientApp(clientId: string, id: string): Promise<void> {
  await fetchAppSuiteUsers(
    `/client/${encodeURIComponent(clientId)}/apps/${encodeURIComponent(id)}`,
    { method: 'DELETE' }
  )
}

export async function lookupClientApps(
  clientId: string,
  payload: { appId: string; apiKey: string }
): Promise<{ appId: string; name: string }[]> {
  const json = await fetchAppSuiteUsers<{ apps?: { appId: string; name: string }[] }>(
    `/client/${encodeURIComponent(clientId)}/apps/lookup`,
    { method: 'POST', body: JSON.stringify(payload) }
  )
  return json.apps || []
}

export async function connectFirebaseApp(
  clientId: string,
  payload: { idToken: string; refreshToken: string; appId: string; name?: string; uid?: string; email?: string }
): Promise<AppSuiteClientApp> {
  const json = await fetchAppSuiteUsers<{ app?: AppSuiteClientApp }>(
    `/client/${encodeURIComponent(clientId)}/apps/connect-firebase`,
    { method: 'POST', body: JSON.stringify(payload) }
  )
  if (!json.app) throw new Error('Failed to connect client app')
  return json.app
}

export async function discoverAccessibleApps(
  clientId: string,
  accessToken: string
): Promise<{ appId: string; name: string }[]> {
  const json = await fetchAppSuiteUsers<{ apps?: { appId: string; name: string }[] }>(
    `/client/${encodeURIComponent(clientId)}/apps/accessible`,
    { method: 'POST', body: JSON.stringify({ accessToken }) }
  )
  return json.apps || []
}

export type UltronCloudFirebaseConfig = {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
  databaseURL: string
}

export async function getUltronCloudFirebaseConfig(): Promise<UltronCloudFirebaseConfig> {
  const json = await fetchAppSuite<{ config?: UltronCloudFirebaseConfig }>('/config/ultroncloud-firebase')
  if (!json.config) throw new Error('UltronCloud Firebase config not available')
  return json.config
}

export async function deployForClient(
  clientId: string,
  payload: { target: 'production' | 'preview'; projectId?: string; productId?: string; targetAppId?: string; releaseNotes?: string }
): Promise<{ success: boolean; error?: string; releaseId?: string; runtimeUrl?: string | null }> {
  const json = await fetchAppSuiteUsers<{ success?: boolean; error?: string; release?: { id?: string; runtimeUrl?: string | null } }>(
    `/client/${encodeURIComponent(clientId)}/deploy`,
    { method: 'POST', body: JSON.stringify(payload) }
  )
  return { success: json.success !== false, error: json.error, releaseId: json.release?.id, runtimeUrl: json.release?.runtimeUrl }
}

export type AppSuiteRelease = {
  id: string
  appId: string
  target: 'production' | 'preview'
  productId?: string | null
  version?: string | null
  status: string
  releaseNotes?: string | null
  runtimeUrl?: string | null
  createdAt?: string | null
  createdBy?: string | null
  rolledBackAt?: string | null
  rolledBackBy?: string | null
  rolledBackReason?: string | null
}

export type AppSuitePublishJob = {
  id: string
  appId: string
  releaseId: string
  channelId?: string | null
  status: string
  triggerAction?: string | null
  errorMessage?: string | null
  createdAt?: string | null
  updatedAt?: string | null
}

export async function getClientReleases(
  clientId: string,
  opts?: { target?: string; limit?: number; projectId?: string }
): Promise<AppSuiteRelease[]> {
  const params = new URLSearchParams()
  if (opts?.target) params.set('target', opts.target)
  if (opts?.limit) params.set('limit', String(opts.limit))
  if (opts?.projectId) params.set('projectId', opts.projectId)
  const query = params.toString()
  const json = await fetchAppSuiteUsers<{ releases?: AppSuiteRelease[] }>(
    `/client/${encodeURIComponent(clientId)}/releases${query ? `?${query}` : ''}`
  )
  return json.releases ?? []
}

export async function rollbackClientRelease(
  clientId: string,
  releaseId: string,
  reason?: string
): Promise<{ success: boolean; error?: string }> {
  const json = await fetchAppSuiteUsers<{ success?: boolean; error?: string }>(
    `/client/${encodeURIComponent(clientId)}/releases/${encodeURIComponent(releaseId)}/rollback`,
    { method: 'POST', body: JSON.stringify({ reason: reason ?? '' }) }
  )
  return { success: json.success !== false, error: json.error }
}

export async function getClientPublishJobs(clientId: string): Promise<AppSuitePublishJob[]> {
  const json = await fetchAppSuiteUsers<{ jobs?: AppSuitePublishJob[] }>(
    `/client/${encodeURIComponent(clientId)}/publish-jobs`
  )
  return json.jobs ?? []
}

export async function removeAppSuiteUser(
  clientId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> {
  const json = await fetchAppSuiteUsers<{ success?: boolean; error?: string }>(
    `/client/${encodeURIComponent(clientId)}/developers/${encodeURIComponent(userId)}`,
    { method: 'DELETE' }
  )
  return { success: json.success !== false, error: json.error }
}

export async function revokeAppSuiteInvitation(
  clientId: string,
  invitationId: string
): Promise<{ success: boolean; error?: string }> {
  const json = await fetchAppSuiteUsers<{ success?: boolean; error?: string }>(
    `/client/${encodeURIComponent(clientId)}/invitations/${encodeURIComponent(invitationId)}`,
    { method: 'DELETE' }
  )
  return { success: json.success !== false, error: json.error }
}

export async function createAppSuiteSharePreview(projectId: string): Promise<{ token: string; expiresAt: string }> {
  const json = await fetchAppSuite<{ success?: boolean; token?: string; expiresAt?: string; error?: string }>(
    `/projects/${encodeURIComponent(projectId)}/share-preview`,
    { method: 'POST' }
  )
  if (!json.token) throw new Error(json.error || 'Failed to create share preview')
  return { token: json.token, expiresAt: json.expiresAt! }
}

export type AppSuiteSharePreviewSnapshot = {
  projectId: string
  appId: string
  templateId: string
  styleOverrides: Record<string, unknown>
  expiresAt: string
}

export async function getAppSuiteSharePreview(token: string): Promise<AppSuiteSharePreviewSnapshot | null> {
  try {
    const apiBase = getCurrentApiUrl()
    const res = await fetch(`${apiBase}/appsuite-public/project-preview/${encodeURIComponent(token)}`)
    if (!res.ok) return null
    const json = await res.json() as { success?: boolean; snapshot?: AppSuiteSharePreviewSnapshot }
    return json.snapshot ?? null
  } catch {
    return null
  }
}

export type AppSuiteRuntimeConfig = {
  appId: string
  targetAppId: string | null
  templateId: string
  renderModel: Record<string, unknown>
  runtimeUrl: string
  releaseId: string
  version: string
  deployedAt: string
}

export async function getRuntimeConfig(appId: string): Promise<AppSuiteRuntimeConfig | null> {
  try {
    const apiBase = getCurrentApiUrl()
    const res = await fetch(`${apiBase}/appsuite-public/runtime/${encodeURIComponent(appId)}`)
    if (!res.ok) return null
    const json = await res.json() as { success?: boolean } & Partial<AppSuiteRuntimeConfig>
    if (!json.success || !json.templateId) return null
    return {
      appId: json.appId!,
      targetAppId: json.targetAppId ?? null,
      templateId: json.templateId,
      renderModel: json.renderModel ?? {},
      runtimeUrl: json.runtimeUrl ?? '',
      releaseId: json.releaseId ?? '',
      version: json.version ?? '',
      deployedAt: json.deployedAt ?? '',
    }
  } catch {
    return null
  }
}
